import { useEffect, useState } from "react";
import QRCode from "qrcode";
import {
  beginMfaSetup,
  changePassword,
  completeMfaLogin,
  deleteAccount,
  disableMfa,
  enableMfa,
  getAccountSessions,
  getMfaStatus,
  getOrCreateAuthSession,
  isMfaLoginChallenge,
  loginPasswordAccount,
  logoutOtherAccountSessions,
  logoutPasswordAccount,
  registerPasswordAccount,
  requestEmailVerification,
  requestPasswordReset,
  resetPassword,
  revokeAccountSession,
  verifyEmailToken
} from "../../api/auth-client";
import type {
  AccountSession,
  AuthSessionResponse,
  MfaSetup,
  MfaStatus
} from "../../api/auth.types";
import type { OmnisyniaClientModule } from "../module.types";

type AccessMode = "login" | "register" | "forgot";

function AccountModule() {
  const [session, setSession] = useState<AuthSessionResponse>();
  const [sessions, setSessions] = useState<readonly AccountSession[]>([]);
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>();
  const [mfaSetup, setMfaSetup] = useState<MfaSetup>();
  const [mfaQr, setMfaQr] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[]>([]);
  const [accessMode, setAccessMode] = useState<AccessMode>("login");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [mfaChallengeToken, setMfaChallengeToken] = useState("");
  const [mfaLoginCode, setMfaLoginCode] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changeMfaCode, setChangeMfaCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteMfaCode, setDeleteMfaCode] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    void initialize();
  }, []);

  async function initialize() {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("accountAction");
    const actionToken = params.get("token") ?? "";
    if (action === "reset-password" && actionToken) {
      setResetToken(actionToken);
      removeAccountActionFromUrl();
    }
    if (action === "verify-email" && actionToken) {
      setBusy(true);
      try {
        await verifyEmailToken(actionToken);
        setNotice("Correo verificado correctamente. Tu cuenta ya está confirmada.");
      } catch (err) {
        setError(messageFrom(err, "No se pudo verificar el correo."));
      } finally {
        removeAccountActionFromUrl();
        setBusy(false);
      }
    }
    try {
      const activeSession = await getOrCreateAuthSession();
      setSession(activeSession);
      if (activeSession.user.mode === "account") await loadSecurity(activeSession);
    } catch (err) {
      setError(messageFrom(err, "No se pudo cargar tu cuenta."));
    }
  }

  async function loadSecurity(activeSession: AuthSessionResponse) {
    const [nextSessions, nextMfaStatus] = await Promise.all([
      getAccountSessions(activeSession.sessionToken),
      getMfaStatus(activeSession.sessionToken)
    ]);
    setSessions(nextSessions);
    setMfaStatus(nextMfaStatus);
  }

  async function submitAccess() {
    setBusy(true);
    clearMessages();
    try {
      if (accessMode === "forgot") {
        const result = await requestPasswordReset(email);
        setNotice(result.deliveryConfigured
          ? "Si existe una cuenta con ese correo, recibirás un enlace válido durante 30 minutos."
          : "La recuperación está preparada, pero el envío de correos todavía debe activarse en el servidor.");
        return;
      }
      const result = accessMode === "register"
        ? await registerPasswordAccount({ email, password, displayName })
        : await loginPasswordAccount({ email, password });
      if (isMfaLoginChallenge(result)) {
        setMfaChallengeToken(result.challengeToken);
        setNotice("Introduce tu segundo factor para terminar de entrar.");
        return;
      }
      setSession(result);
      setPassword("");
      await loadSecurity(result);
      setNotice(accessMode === "register" ? "Cuenta creada correctamente." : "Sesión iniciada de forma segura.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo completar el acceso."));
    } finally {
      setBusy(false);
    }
  }

  async function submitMfaLogin() {
    setBusy(true);
    clearMessages();
    try {
      const activeSession = await completeMfaLogin(mfaChallengeToken, mfaLoginCode);
      setRecoveryCodes([]);
      setSession(activeSession);
      setMfaChallengeToken("");
      setMfaLoginCode("");
      await loadSecurity(activeSession);
      setNotice("Identidad confirmada. Bienvenido de nuevo.");
    } catch (err) {
      setError(messageFrom(err, "El código no se pudo comprobar."));
    } finally {
      setBusy(false);
    }
  }

  async function submitResetPassword() {
    setBusy(true);
    clearMessages();
    try {
      await resetPassword(resetToken, resetPasswordValue);
      setResetToken("");
      setResetPasswordValue("");
      removeAccountActionFromUrl();
      setSession(undefined);
      setNotice("Contraseña actualizada. Ya puedes iniciar sesión con la nueva.");
      setAccessMode("login");
    } catch (err) {
      setError(messageFrom(err, "No se pudo actualizar la contraseña."));
    } finally {
      setBusy(false);
    }
  }

  async function sendVerification() {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      const result = await requestEmailVerification(session.sessionToken);
      if (result.alreadyVerified) setNotice("Tu correo ya estaba verificado.");
      else if (result.delivered) setNotice("Enlace enviado. Revisa tu bandeja de entrada y la carpeta de spam.");
      else setNotice("La verificación está preparada, pero el envío de correos todavía debe activarse en el servidor.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo solicitar la verificación."));
    } finally {
      setBusy(false);
    }
  }

  async function startMfaSetup() {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      const setup = await beginMfaSetup(session.sessionToken);
      setMfaSetup(setup);
      setMfaQr(await QRCode.toDataURL(setup.provisioningUri, { width: 260, margin: 2, errorCorrectionLevel: "M" }));
      setNotice("Escanea el código y escribe el número de 6 cifras para activarlo.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo iniciar la configuración."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmMfaSetup() {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      const result = await enableMfa(session.sessionToken, mfaCode);
      setRecoveryCodes(result.recoveryCodes);
      setMfaSetup(undefined);
      setMfaQr("");
      setMfaCode("");
      await loadSecurity(session);
      setSession({ ...session, user: { ...session.user, mfaEnabled: true } });
      setNotice("Doble verificación activada. Guarda ahora tus códigos de respaldo.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo activar la doble verificación."));
    } finally {
      setBusy(false);
    }
  }

  async function turnOffMfa() {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      await disableMfa(session.sessionToken, disablePassword, disableCode);
      setDisablePassword("");
      setDisableCode("");
      setRecoveryCodes([]);
      await loadSecurity(session);
      setSession({ ...session, user: { ...session.user, mfaEnabled: false } });
      setNotice("Doble verificación desactivada.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo desactivar la doble verificación."));
    } finally {
      setBusy(false);
    }
  }

  async function submitPasswordChange() {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      await changePassword(session.sessionToken, {
        currentPassword,
        newPassword,
        mfaCode: changeMfaCode || undefined
      });
      setCurrentPassword("");
      setNewPassword("");
      setChangeMfaCode("");
      await loadSecurity(session);
      setNotice("Contraseña actualizada. Las demás sesiones se han cerrado.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo cambiar la contraseña."));
    } finally {
      setBusy(false);
    }
  }

  async function closeOtherSessions() {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      const count = await logoutOtherAccountSessions(session.sessionToken);
      await loadSecurity(session);
      setNotice(count ? `${count} sesión(es) cerrada(s).` : "No había otras sesiones abiertas.");
    } catch (err) {
      setError(messageFrom(err, "No se pudieron cerrar las otras sesiones."));
    } finally {
      setBusy(false);
    }
  }

  async function closeSession(sessionId: string) {
    if (!session) return;
    setBusy(true);
    clearMessages();
    try {
      await revokeAccountSession(session.sessionToken, sessionId);
      await loadSecurity(session);
      setNotice("Sesión cerrada.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo cerrar la sesión."));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    if (!session) return;
    setBusy(true);
    let confirmed = true;
    try {
      await logoutPasswordAccount(session.sessionToken);
    } catch {
      confirmed = false;
    } finally {
      setSession(undefined);
      setSessions([]);
      setMfaStatus(undefined);
      setMfaSetup(undefined);
      setMfaQr("");
      setMfaCode("");
      setRecoveryCodes([]);
      setNotice(confirmed ? "Sesión cerrada." : "Sesión cerrada en este dispositivo.");
      setBusy(false);
    }
  }

  async function removeAccount() {
    if (!session || deleteConfirmation !== "ELIMINAR") return;
    setBusy(true);
    clearMessages();
    try {
      await deleteAccount(session.sessionToken, deletePassword, deleteMfaCode || undefined);
      setSession(undefined);
      setSessions([]);
      setMfaStatus(undefined);
      setMfaSetup(undefined);
      setMfaQr("");
      setMfaCode("");
      setRecoveryCodes([]);
      setNotice("La cuenta y sus datos personales se han eliminado.");
    } catch (err) {
      setError(messageFrom(err, "No se pudo eliminar la cuenta."));
    } finally {
      setBusy(false);
    }
  }

  function clearMessages() {
    setNotice(undefined);
    setError(undefined);
  }

  const isAccount = session?.user.mode === "account";

  return (
    <section className="editor-shell account-shell" aria-labelledby="account-title">
      <header className="editor-header">
        <div>
          <p className="eyebrow">IDENTIDAD Y SEGURIDAD</p>
          <h2 id="account-title">Mi cuenta</h2>
          <p>Controla tu acceso, contraseña, doble verificación y dispositivos conectados.</p>
        </div>
        {isAccount && <button className="secondary-button" disabled={busy} onClick={() => void signOut()} type="button">Cerrar sesión</button>}
      </header>

      {resetToken ? (
        <article className="account-card account-access-card">
          <span className="account-icon">↻</span>
          <div>
            <h3>Crea una contraseña nueva</h3>
            <p>Debe tener al menos 12 caracteres y combinar tres tipos de caracteres.</p>
            <label>Nueva contraseña<input autoComplete="new-password" minLength={12} type="password" value={resetPasswordValue} onChange={(event) => setResetPasswordValue(event.target.value)} /></label>
            <button className="primary-button" disabled={busy || resetPasswordValue.length < 12} onClick={() => void submitResetPassword()} type="button">Guardar contraseña</button>
          </div>
        </article>
      ) : !isAccount ? (
        <article className="account-card account-access-card">
          <span className="account-icon">◇</span>
          <div>
            <h3>{accessMode === "register" ? "Crea tu cuenta personal" : accessMode === "forgot" ? "Recupera tu cuenta" : "Entra en OMNISYNIA"}</h3>
            {mfaChallengeToken ? (
              <div className="account-form">
                <p>Abre tu aplicación Authenticator o utiliza uno de tus códigos de respaldo.</p>
                <label>Código de verificación<input autoComplete="one-time-code" inputMode="numeric" value={mfaLoginCode} onChange={(event) => setMfaLoginCode(event.target.value)} /></label>
                <div className="account-actions">
                  <button className="primary-button" disabled={busy || !mfaLoginCode} onClick={() => void submitMfaLogin()} type="button">Confirmar identidad</button>
                  <button className="secondary-button" onClick={() => { setMfaChallengeToken(""); setMfaLoginCode(""); }} type="button">Volver</button>
                </div>
              </div>
            ) : (
              <div className="account-form">
                {accessMode === "register" && <label>Nombre y apellidos<input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>}
                <label>Correo<input autoComplete="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
                {accessMode !== "forgot" && <label>Contraseña<input autoComplete={accessMode === "register" ? "new-password" : "current-password"} minLength={12} type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>}
                <button className="primary-button" disabled={busy || !email || (accessMode !== "forgot" && password.length < 12)} onClick={() => void submitAccess()} type="button">
                  {accessMode === "register" ? "Crear mi cuenta" : accessMode === "forgot" ? "Enviar enlace" : "Entrar"}
                </button>
                <div className="account-mode-links">
                  {accessMode !== "login" && <button onClick={() => setAccessMode("login")} type="button">Ya tengo cuenta</button>}
                  {accessMode !== "register" && <button onClick={() => setAccessMode("register")} type="button">Crear cuenta</button>}
                  {accessMode !== "forgot" && <button onClick={() => setAccessMode("forgot")} type="button">Olvidé mi contraseña</button>}
                </div>
              </div>
            )}
          </div>
        </article>
      ) : session && (
        <div className="account-dashboard">
          <article className="account-card account-profile-card">
            <div>
              <span className="eyebrow">CUENTA PERSONAL</span>
              <h3>{session.user.displayName}</h3>
              <p>{session.user.email}</p>
            </div>
            <div className="account-statuses">
              <span className={session.user.emailVerified ? "account-status ok" : "account-status warning"}>{session.user.emailVerified ? "Correo verificado" : "Correo pendiente"}</span>
              <span className={mfaStatus?.enabled ? "account-status ok" : "account-status warning"}>{mfaStatus?.enabled ? "Doble factor activo" : "Doble factor pendiente"}</span>
              {mfaStatus?.founderRole && <span className="account-status founder">{mfaStatus.founderRole === "creator" ? "Creador" : "Socio fundador"}</span>}
            </div>
            {!session.user.emailVerified && <button className="secondary-button" disabled={busy} onClick={() => void sendVerification()} type="button">Verificar correo</button>}
          </article>

          <article className="account-card">
            <div className="account-card-heading">
              <div><span className="eyebrow">PROTECCIÓN RECOMENDADA</span><h3>Doble verificación</h3></div>
              <span className={mfaStatus?.enabled ? "account-status ok" : "account-status warning"}>{mfaStatus?.enabled ? "Activada" : "Desactivada"}</span>
            </div>
            {mfaStatus?.recommended && !mfaStatus.enabled && <p className="account-callout">Esta protección es especialmente importante para las dos cuentas fundadoras.</p>}
            {!mfaStatus?.enabled && !mfaSetup && <button className="primary-button account-fit-button" disabled={busy} onClick={() => void startMfaSetup()} type="button">Configurar Authenticator</button>}
            {mfaSetup && (
              <div className="mfa-setup-grid">
                <div className="mfa-qr">{mfaQr && <img alt="Código QR para Authenticator" src={mfaQr} />}</div>
                <div className="account-form">
                  <p>1. Escanea el QR con Google Authenticator, Microsoft Authenticator, 1Password o similar.</p>
                  <p>2. Si no puedes escanearlo, introduce esta clave:</p>
                  <code>{mfaSetup.secret}</code>
                  <label>Código de 6 cifras<input autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} /></label>
                  <button className="primary-button" disabled={busy || mfaCode.length !== 6} onClick={() => void confirmMfaSetup()} type="button">Activar doble verificación</button>
                </div>
              </div>
            )}
            {recoveryCodes.length > 0 && (
              <div className="recovery-panel">
                <strong>Guarda estos códigos una sola vez</strong>
                <p>Permiten entrar si pierdes el teléfono. Cada código funciona una vez.</p>
                <div>{recoveryCodes.map((code) => <code key={code}>{code}</code>)}</div>
                <button
                  className="secondary-button account-fit-button"
                  onClick={() => {
                    setRecoveryCodes([]);
                    setNotice("Códigos ocultados. Usa la copia segura que guardaste.");
                  }}
                  type="button"
                >
                  Ya los he guardado
                </button>
              </div>
            )}
            {mfaStatus?.enabled && (
              <details className="account-details">
                <summary>Desactivar doble verificación</summary>
                <div className="account-form compact">
                  <label>Contraseña actual<input autoComplete="current-password" type="password" value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} /></label>
                  <label>Código actual o de respaldo<input autoComplete="one-time-code" value={disableCode} onChange={(event) => setDisableCode(event.target.value)} /></label>
                  <button className="danger-button" disabled={busy || !disablePassword || !disableCode} onClick={() => void turnOffMfa()} type="button">Desactivar</button>
                </div>
              </details>
            )}
          </article>

          <article className="account-card">
            <span className="eyebrow">CONTRASEÑA</span>
            <h3>Cambiar contraseña</h3>
            <div className="account-form account-form-columns">
              <label>Contraseña actual<input autoComplete="current-password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
              <label>Nueva contraseña<input autoComplete="new-password" minLength={12} type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
              {mfaStatus?.enabled && <label>Código de doble factor<input autoComplete="one-time-code" value={changeMfaCode} onChange={(event) => setChangeMfaCode(event.target.value)} /></label>}
              <button className="secondary-button" disabled={busy || !currentPassword || newPassword.length < 12 || (mfaStatus?.enabled && !changeMfaCode)} onClick={() => void submitPasswordChange()} type="button">Actualizar contraseña</button>
            </div>
          </article>

          <article className="account-card">
            <div className="account-card-heading">
              <div><span className="eyebrow">DISPOSITIVOS</span><h3>Sesiones activas</h3></div>
              <button className="secondary-button" disabled={busy || sessions.length <= 1} onClick={() => void closeOtherSessions()} type="button">Cerrar las demás</button>
            </div>
            <div className="account-session-list">
              {sessions.map((item) => (
                <div key={item.id}>
                  <span><strong>{item.current ? "Este dispositivo" : "Otro dispositivo"}</strong><small>Último uso: {new Date(item.lastSeenAt).toLocaleString("es-ES")}</small></span>
                  {!item.current && <button disabled={busy} onClick={() => void closeSession(item.id)} type="button">Cerrar</button>}
                </div>
              ))}
            </div>
          </article>

          <details className="account-card danger-zone">
            <summary>Eliminar mi cuenta</summary>
            <p>Esta acción elimina los datos personales y cierra todas las sesiones. Las cuentas fundadoras están protegidas y no pueden borrarse accidentalmente.</p>
            <div className="account-form account-form-columns">
              <label>Contraseña<input autoComplete="current-password" type="password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} /></label>
              {mfaStatus?.enabled && <label>Código de doble factor<input autoComplete="one-time-code" value={deleteMfaCode} onChange={(event) => setDeleteMfaCode(event.target.value)} /></label>}
              <label>Escribe ELIMINAR<input value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} /></label>
              <button className="danger-button" disabled={busy || !deletePassword || deleteConfirmation !== "ELIMINAR" || (mfaStatus?.enabled && !deleteMfaCode)} onClick={() => void removeAccount()} type="button">Eliminar definitivamente</button>
            </div>
          </details>
        </div>
      )}

      {notice && <p className="success-message account-message">{notice}</p>}
      {error && <p className="error-message account-message">{error}</p>}
    </section>
  );
}

function removeAccountActionFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("accountAction");
  url.searchParams.delete("token");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function messageFrom(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export const accountClientModule: OmnisyniaClientModule = {
  id: "account",
  displayName: "Mi cuenta",
  description: "Identidad, acceso y seguridad personal.",
  tier: "core",
  status: "available",
  Component: AccountModule
};
